// Shared browser runtime for the no-code Payment Button and the programmatic
// checkout() API. Handles config resolution, button rendering, and the
// popup-window checkout (with redirect fallback) + postMessage completion.

declare const process: { env?: Record<string, string | undefined> } | undefined;

const BAKED_API_BASE =
  (typeof process !== 'undefined' && process?.env?.API_BASE_URL) || '';

export interface PaymentButtonConfig {
  buttonId: string;
  label: string;
  amountType: 'fixed' | 'customer';
  amount: number | null;
  currency: string;
  theme: 'light' | 'dark' | 'brand';
  primaryColor: string;
  merchantName: string;
}

export interface CheckoutResult {
  status: 'success' | 'failed' | 'pending' | 'cancelled';
  paymentId?: number;
  reference?: string;
}

export interface CheckoutOptions {
  /** Amount for customer-entered buttons. Ignored by fixed-amount buttons. */
  amount?: number;
  /** Optional merchant order reference attached to the payment. */
  reference?: string;
  /** Force a full-page redirect instead of a popup. */
  redirect?: boolean;
  /**
   * Popup window pre-opened synchronously inside the click handler (via
   * openPopupShell). Avoids popup blockers that reject windows opened after
   * an async gap (Safari, and Chrome on embedded sites like Squarespace).
   */
  popup?: Window | null;
}

/**
 * Resolve the API base URL. Order of precedence:
 *   1. window.ACOUNTPAY_API_BASE (runtime override, useful for staging)
 *   2. the value baked in at build time
 *   3. production default
 */
export function resolveApiBase(): string {
  const runtime =
    typeof window !== 'undefined' &&
    (window as any).ACOUNTPAY_API_BASE;
  return (runtime || BAKED_API_BASE || 'https://api.acountpay.com/v1').replace(
    /\/+$/,
    '',
  );
}

function uuid(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function fetchButtonConfig(
  buttonId: string,
  apiBase = resolveApiBase(),
): Promise<PaymentButtonConfig> {
  const res = await fetch(
    `${apiBase}/sdk/v1/payment-buttons/${encodeURIComponent(buttonId)}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  );
  if (!res.ok) {
    throw new Error(`Failed to resolve payment button (${res.status})`);
  }
  return res.json();
}

async function createCheckoutUrl(
  buttonId: string,
  opts: { amount?: number; reference?: string; mode: 'popup' | 'redirect' },
  apiBase = resolveApiBase(),
): Promise<{ redirectUrl: string; paymentId: number }> {
  const res = await fetch(
    `${apiBase}/sdk/v1/payment-buttons/${encodeURIComponent(buttonId)}/checkout`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': uuid(),
      },
      body: JSON.stringify({
        amount: opts.amount,
        reference: opts.reference,
        mode: opts.mode,
      }),
    },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Checkout failed (${res.status})${body ? ` - ${body}` : ''}`);
  }
  return res.json();
}

function isLikelyMobile(): boolean {
  if (typeof window === 'undefined') return false;
  const narrow = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  return Boolean(narrow && touch);
}

const POPUP_FEATURES = (width: number, height: number, left: number, top: number) =>
  `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

/**
 * Synchronously open an empty checkout popup. Must be called directly inside
 * a user-gesture handler (click), BEFORE any await, so popup blockers allow
 * it. The window is later navigated to the hosted pay page by startCheckout.
 *
 * Returns null on mobile (where we redirect instead) or when blocked.
 */
export function openPopupShell(): Window | null {
  if (typeof window === 'undefined' || isLikelyMobile()) return null;
  const width = 480;
  const height = 720;
  const left = Math.max(0, (window.screen.width - width) / 2 + (window.screenX || 0));
  const top = Math.max(0, (window.screen.height - height) / 2 + (window.screenY || 0));
  const popup = window.open(
    '',
    'acountpay_checkout',
    POPUP_FEATURES(width, height, left, top),
  );
  if (!popup || popup.closed || typeof popup.closed === 'undefined') return null;
  try {
    popup.document.write(
      '<!doctype html><html><head><title>AcountPay</title></head>' +
        '<body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;' +
        'font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;color:#0F172A">' +
        '<div>Loading secure payment&hellip;</div></body></html>',
    );
    popup.document.close();
  } catch {
    /* ignore: document may be cross-origin if window was reused */
  }
  return popup;
}

/**
 * Open the hosted pay page in a popup window with a dimmed overlay on the
 * parent. Resolves when the popup reports completion via postMessage, the
 * popup is closed, or (fallback) we redirect the top window.
 *
 * A popup (not an iframe) is required: the hosted pay page and bank SCA pages
 * set X-Frame-Options/frame-ancestors that forbid framing.
 */
export function openCheckoutPopup(
  redirectUrl: string,
  options: { forceRedirect?: boolean; popup?: Window | null } = {},
): Promise<CheckoutResult> {
  const expectedOrigin = (() => {
    try {
      return new URL(redirectUrl).origin;
    } catch {
      return '*';
    }
  })();

  // Mobile and forced cases: redirect the top window. The hosted page returns
  // the customer to the merchant's configured success URL with ?status=.
  if (options.forceRedirect || isLikelyMobile()) {
    window.location.assign(redirectUrl);
    return new Promise(() => {
      /* navigation pending; never resolves */
    });
  }

  // Reuse a pre-opened shell (popup-blocker safe path) or open a new window.
  let popup: Window | null = null;
  if (options.popup && !options.popup.closed) {
    popup = options.popup;
    try {
      popup.location.replace(redirectUrl);
    } catch {
      popup.location.href = redirectUrl;
    }
  } else {
    const width = 480;
    const height = 720;
    const left = Math.max(0, (window.screen.width - width) / 2 + (window.screenX || 0));
    const top = Math.max(0, (window.screen.height - height) / 2 + (window.screenY || 0));
    popup = window.open(
      redirectUrl,
      'acountpay_checkout',
      POPUP_FEATURES(width, height, left, top),
    );
  }

  // Popup blocked: fall back to a full-page redirect.
  if (!popup || popup.closed || typeof popup.closed === 'undefined') {
    window.location.assign(redirectUrl);
    return new Promise(() => {
      /* navigation pending */
    });
  }

  const overlay = createOverlay();
  document.body.appendChild(overlay);

  return new Promise<CheckoutResult>((resolve) => {
    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(closedTimer);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      try {
        if (popup && !popup.closed) popup.close();
      } catch {
        /* cross-origin close guard */
      }
    };
    const finish = (result: CheckoutResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      if (expectedOrigin !== '*' && event.origin !== expectedOrigin) return;
      const data = event.data;
      if (!data || data.source !== 'acountpay') return;
      finish({
        status: data.status,
        paymentId: data.paymentId,
        reference: data.reference,
      });
    };
    window.addEventListener('message', onMessage);

    // If the customer closes the popup manually, treat it as cancelled.
    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        finish({ status: 'cancelled' });
      }
    }, 600);

    overlay.querySelector('[data-acountpay-cancel]')?.addEventListener(
      'click',
      () => finish({ status: 'cancelled' }),
    );
  });
}

function createOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.setAttribute('data-acountpay-overlay', '');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483646',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:16px',
    'background:rgba(15,23,42,0.55)',
    'backdrop-filter:blur(2px)',
    'font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
  ].join(';');
  overlay.innerHTML = `
    <div style="color:#fff;font-size:15px;font-weight:500;text-align:center;max-width:280px;line-height:1.5">
      Complete your payment in the popup window
    </div>
    <button data-acountpay-cancel style="appearance:none;border:1px solid rgba(255,255,255,0.4);background:transparent;color:#fff;padding:8px 18px;border-radius:8px;font-size:14px;cursor:pointer">
      Cancel
    </button>`;
  return overlay;
}

/**
 * Programmatic checkout entry point. Resolves to a CheckoutResult (popup mode)
 * or never resolves (redirect mode, because the page navigates away).
 */
export async function startCheckout(
  buttonId: string,
  options: CheckoutOptions = {},
): Promise<CheckoutResult> {
  const shell = options.popup && !options.popup.closed ? options.popup : null;

  // Decide popup vs redirect up front so the backend can route the bank
  // callback to the right place (POS bridge for popup, merchant URL for
  // redirect). Mobile and forced cases always redirect; a pre-opened shell
  // always means popup.
  const useRedirect = !shell && (Boolean(options.redirect) || isLikelyMobile());
  const mode: 'popup' | 'redirect' = useRedirect ? 'redirect' : 'popup';

  let redirectUrl: string;
  try {
    ({ redirectUrl } = await createCheckoutUrl(buttonId, {
      amount: options.amount,
      reference: options.reference,
      mode,
    }));
  } catch (err) {
    // Close the loading shell so the customer isn't left on a blank window.
    try {
      shell?.close();
    } catch {
      /* ignore */
    }
    throw err;
  }
  return openCheckoutPopup(redirectUrl, { forceRedirect: useRedirect, popup: shell });
}
